import { useCallback, useEffect, useMemo, useState } from 'react';
import { appeler, ErreurApi } from './api';
import type { ContexteCarte } from './carte';
import { libelleOrigineParcelle, surfaceEnFrancais } from '../../src/partage/libelles';

/**
 * LE PANNEAU DE LA CASCADE — pays › collectivité › section › parcelle.
 *
 * ⚠️⚠️ IL REMPLACE UN ÉCRAN ENTIER, ET C'EST TOUT SON OBJET. Jusqu'au
 * 22/09/2026, le plan cadastral vivait dans `assemblage.tsx` : 1 290 lignes de
 * `<canvas>` qui redessinaient à la main les mêmes sections que la carte
 * MapLibre, depuis une autre route, avec leur propre cadrage, leur propre
 * zoom, et leur propre texte d'attribution ODbL — l'un calculé dans
 * `carte.tsx`, l'autre écrit en dur ici. Deux représentations du même objet
 * divergent au premier correctif appliqué à une seule : c'est le défaut du SDK
 * de Connect, forké en deux exemplaires qui ont dérivé.
 *
 * ⚠️ CE QUI EST GARDÉ, ET CE QUI EST JETÉ. Le canvas est jeté — MapLibre sert
 * déjà les sections en tuiles vectorielles, avec leur origine, leur
 * collectivité et leur identifiant. Tout le RESTE est porté ici : le tableau
 * d'assemblage, la statistique de la collectivité, la fiche de section, le
 * découpage en feuilles, les deux impressions, la liste des parcelles et la
 * phrase qui explique pourquoi une limite est droite. Aucune de ces dix choses
 * n'est une carte : ce sont un registre et un plan de tirage.
 *
 * ⚠️⚠️ L'ÉCHELON NE SUIT PLUS CE QUE LA CARTE REGARDE — item D.997, tranché
 * par le fondateur le 24/09/2026. La doctrine qui vivait ici (« il suit ce que
 * la carte regarde ») faisait changer ce panneau au simple DÉFILEMENT : ça se
 * lisait comme une sélection qu'on n'a pas faite, et le contrat §59 lie
 * l'accès à la parcelle SÉLECTIONNÉE, jamais au centrage. Désormais :
 *   - la collectivité sous le centre s'affiche dans la BARRE D'ÉTAT (position) ;
 *   - ce panneau n'obéit qu'aux GESTES : clic sur la carte (le point du clic
 *     donne la pile entière), clic dans la liste du pays, fil d'Ariane, ou
 *     « Ouvrir » sur la position de la barre d'état.
 * Le souci que l'ancienne doctrine couvrait — deux communes portent les mêmes
 * codes de section (1 515 sur 1 552) — est tenu par l'adoption AU CLIC : la
 * section et sa collectivité sont choisies au même point, elles ne peuvent
 * plus diverger par la navigation.
 */

/** Une ligne du tableau d'assemblage — ce que sert `GET /sections/`. */
interface LigneSection {
  code: string;
  hectares: string;
  origine: 'voie' | 'grille';
  cellule_m: number;
  gelee_le: string | null;
  parcelles: number;
}
interface Assemblage {
  collectivite: { code: string; nom: string; echelon: string; source_id: number };
  assemblage: { sections: number; hectares: string | null; gelees: number };
  sections: LigneSection[];
  /** Vrai si la liste a été plafonnée — l'écran le DIT plutôt que de tronquer. */
  tronque: boolean;
}
interface LigneCollectivite {
  source_id: number; nom: string; code: string | null; echelon: string;
  sections: number; parcelles: number; hectares: string; gelees: number;
  /** Le plan de levé — migration 219. */
  sections_grille: number; batiments: number; batiments_sous_grille: number;
  part_carroyage: number | null; rang_leve: number;
  contour: { type: string; coordinates: unknown };
}
interface VueDuPays {
  collectivites: number; sections: number; parcelles: number;
  /** Le carroyage national, dans les DEUX unités — l'écart est le message. */
  carroyage: { sections: number; batiments: number; batimentsTotal: number };
  /** Les décomptes viennent d'une vue matérialisée : leur âge est servi avec eux. */
  figeLe: string | null;
  lignes: LigneCollectivite[];
}
interface Feuilles {
  section: string; format: string; formatLibelle: string; echelle: number;
  feuilles: number; colonnes: number; lignes: number; parcelles: number;
  gelee: boolean; vide: boolean;
  /** Où tombent les feuilles — servi par la route, JAMAIS recalculé ici. */
  empriseDesFeuilles: Array<{ feuille: number; anneau: Array<[number, number]> }>;
}

/** Ce que la carte dessine du tirage — l'emprise servie, telle quelle. */
export interface CadreFeuilles {
  echelle: number;
  emprises: Array<{ feuille: number; anneau: Array<[number, number]> }>;
}

export interface CibleCarte { centre: [number, number]; zoom: number; cle: string }

const nb = (v: number, d = 1) => v.toLocaleString('fr-FR', { maximumFractionDigits: d });
const messageDe = (e: unknown) => (e instanceof ErreurApi ? e.message : String(e));

/**
 * LE CENTRE D'UN CONTOUR, pour y voler.
 *
 * ⚠️ LA BOÎTE ENGLOBANTE, PAS LE PREMIER POINT. Le centre d'une boîte tombe
 * dans le vide pour une commune en croissant — c'est le défaut « Y aller
 * déposait sur du vide », mesuré à 0 parcelle dans un rayon de 500 m. Ici le
 * risque est moindre (on vole vers une commune, pas vers une parcelle) et le
 * coût d'un `ST_PointOnSurface` côté client serait disproportionné : on
 * accepte la boîte, et le vol s'arrête à z11 — l'échelon où la commune entière
 * tient à l'écran, donc où être légèrement décentré ne fait rien manquer.
 */
function centreDuContour(g: { type: string; coordinates: unknown }): [number, number] | null {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const parcourir = (n: unknown): void => {
    if (!Array.isArray(n)) return;
    if (typeof n[0] === 'number' && typeof n[1] === 'number') {
      const [x, y] = n as [number, number];
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
      return;
    }
    for (const e of n) parcourir(e);
  };
  parcourir(g.coordinates);
  if (!Number.isFinite(minX)) return null;
  return [(minX + maxX) / 2, (minY + maxY) / 2];
}

/* ════════════════════════════════════════════════════════════════════════════
   LE PAYS — les collectivités qui portent un plan cadastral.
   ════════════════════════════════════════════════════════════════════════════
   ⚠️ C'ÉTAIT LA PORTE D'ENTRÉE QUI MANQUAIT, et son absence se voyait à ce que
   l'écran demandait : « collectivité — ex. 84 ». Il fallait SAVOIR le numéro
   de la commune cherchée, c'est-à-dire détenir déjà la réponse.

   ⚠️ ON NE REND QUE CE QUI PORTE DES SECTIONS. `ter` compte 386
   circonscriptions ; 331 portent du cadastre. Lister les autres ferait cliquer
   sur des communes dont le plan n'existe pas, et l'écran rendrait une erreur
   là où il n'y a qu'une absence de découpage.
*/
function VueDuPaysListe(
  { onAller, onChoisir }:
  { onAller: (c: CibleCarte) => void; onChoisir: (c: ContexteCarte['collectivite']) => void },
) {
  const [v, setV] = useState<VueDuPays | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [filtre, setFiltre] = useState('');
  /**
   * ⚠️ « LEVÉ » EST LE TRI PAR DÉFAUT, et ce n'est pas un choix d'ergonomie.
   * Un registre d'État ouvert sur l'ordre alphabétique demande à l'agent de
   * savoir d'avance où aller. Ouvert sur le nombre de bâtiments qui se
   * trouvent dans une section tracée au carroyage, il répond à la seule
   * question qu'un cadastre non levé pose : par où commencer.
   */
  const [tri, setTri] = useState<'leve' | 'parcelles' | 'sections' | 'nom'>('leve');

  useEffect(() => {
    let vivant = true;
    appeler<VueDuPays>('/sections/collectivites')
      .then((r) => { if (vivant) setV(r); })
      .catch((e) => { if (vivant) setErreur(messageDe(e)); });
    return () => { vivant = false; };
  }, []);

  const lignes = useMemo(() => {
    if (!v) return [];
    const f = filtre.trim().toLowerCase();
    const l = v.lignes.filter((x) => !f || x.nom.toLowerCase().includes(f) || (x.code ?? '').toLowerCase().includes(f));
    return [...l].sort((a, b) =>
      tri === 'nom' ? a.nom.localeCompare(b.nom, 'fr')
      : tri === 'sections' ? b.sections - a.sections
      : tri === 'leve' ? b.rang_leve - a.rang_leve
      : b.parcelles - a.parcelles);
  }, [v, filtre, tri]);

  if (erreur) return <p className="erreur" role="alert">{erreur}</p>;
  if (!v) return <p className="mention">Lecture du pays…</p>;

  return (
    <>
      <div className="panneau-cadastral-tete">
        <h3>Le pays</h3>
        <p>
          {v.collectivites.toLocaleString('fr-FR')} collectivités portent un plan cadastral ·{' '}
          {v.sections.toLocaleString('fr-FR')} sections · {v.parcelles.toLocaleString('fr-FR')} parcelles
        </p>
        <input value={filtre} onChange={(e) => setFiltre(e.target.value)}
               placeholder="chercher une commune — ex. Matoto"
               aria-label="Chercher une collectivité" />
        <div className="panneau-cadastral-tri">
          {(['leve', 'parcelles', 'sections', 'nom'] as const).map((t) => (
            <button key={t} type="button" className={`lien${tri === t ? ' actif' : ''}`}
                    onClick={() => setTri(t)}>
              {t === 'nom' ? 'A→Z' : t === 'leve' ? 'à lever' : t}
            </button>
          ))}
        </div>
        {/* ⚠️ Le critère du classement EN CLAIR quand il s'applique — refus
            D.951 : il ne vivait que dans un `title`, donc nulle part au
            clavier. Un classement dont on ne voit pas le critère ne se
            relit pas. */}
        {tri === 'leve' && (
          <p className="mention">
            Classées par bâtiments situés dans une section tracée au carroyage —
            là où le levé manque le plus.
          </p>
        )}
      </div>

      <div className="panneau-cadastral-liste">
        {lignes.map((x) => {
          const centre = centreDuContour(x.contour);
          return (
            <button key={x.source_id} type="button"
                    className={`panneau-cadastral-ligne${x.parcelles === 0 ? ' vide' : ''}`}
                    disabled={!centre}
                    title={centre ? `Aller sur ${x.nom}` : 'Contour inconnu : cette collectivité n’est pas dessinable.'}
                    onClick={() => {
                      if (!centre) return;
                      /* ⚠️ Cliquer une commune la CHOISIT — c'est le geste qui
                         ouvre son assemblage depuis que le panneau ne suit
                         plus le centre (D.997). Le vol vient en plus. */
                      onChoisir({
                        sourceId: x.source_id, nom: x.nom, code: x.code, echelon: x.echelon,
                        sections: x.sections, parcelles: x.parcelles, gelees: x.gelees,
                        peuplee: x.parcelles > 0,
                      });
                      onAller({ centre, zoom: 11, cle: `pays-${x.source_id}-${Date.now()}` });
                    }}>
              <b>{x.nom}</b>
              <i>
                {tri === 'leve'
                  /* ⚠️ LA COLONNE SUIT LE TRI. Classer par « à lever » et
                     afficher un nombre de parcelles obligerait l'agent à
                     croire l'ordre sans pouvoir le vérifier — un classement
                     dont on ne voit pas le critère ne se relit pas. */
                  ? <>
                      {x.batiments_sous_grille === 0
                        ? 'rien à lever'
                        : `${x.batiments_sous_grille.toLocaleString('fr-FR')} bât. à lever`}
                      {x.part_carroyage !== null && ` · ${x.part_carroyage} %`}
                    </>
                  : <>
                      {x.sections.toLocaleString('fr-FR')} sect.
                      {/* ⚠️ « aucune » EN TOUTES LETTRES : un 0 dans une colonne de
                          nombres se lit comme une donnée manquante, et 81 % des
                          sections du pays sont dans ce cas. */}
                      {' · '}{x.parcelles === 0 ? 'aucune parcelle' : `${x.parcelles.toLocaleString('fr-FR')} parc.`}
                    </>}
              </i>
            </button>
          );
        })}
        {lignes.length === 0 && <p className="mention">Aucune collectivité ne porte ce nom.</p>}
      </div>

      {/* ⚠️⚠️ LE CHIFFRE NATIONAL SE DONNE DANS LES DEUX UNITÉS, ET L'ÉCART EST
          LE MESSAGE. Par SECTION le carroyage vaut 99 % — il compte des carrés
          vides au milieu de nulle part. Par BÂTIMENT il vaut 83 %, et c'est
          celui-là qui dit ce qui est en jeu. Servir le premier seul fait
          conclure que tout est à refaire ; servir le second seul cache
          l'étendue du carroyage rural. Mesuré le 22/09/2026 : le réseau viaire
          est ÉPUISÉ (3 rangs, 219 150 tronçons) et n'enclot que 102 îlots dans
          une sous-préfecture de 3 607 km². Le carroyage n'est pas un défaut de
          logiciel : c'est l'absence de levé. */}
      {v.carroyage && v.carroyage.batimentsTotal > 0 && (
        <p className="panneau-cadastral-note avert">
          <strong>
            {Math.round(100 * v.carroyage.sections / Math.max(1, v.sections))} % des sections
            sont tracées au carroyage
          </strong>{' '}— mais, repondéré par ce qui est habité,{' '}
          <strong>
            {Math.round(100 * v.carroyage.batiments / v.carroyage.batimentsTotal)} % du bâti
          </strong>{' '}({v.carroyage.batiments.toLocaleString('fr-FR')} sur{' '}
          {v.carroyage.batimentsTotal.toLocaleString('fr-FR')}). Une section au carroyage n'a
          aucun rapport avec le sol : elle réserve une place en attendant le levé.
        </p>
      )}
      <p className="panneau-cadastral-note">
        {v.lignes.filter((x) => x.parcelles === 0).length.toLocaleString('fr-FR')} collectivité(s) sur{' '}
        {v.collectivites} ont un découpage en sections mais aucune parcelle au registre : le plan
        existe, le cadastre n'y est pas encore levé.
        {/* ⚠️ L'ÂGE DES DÉCOMPTES EST DIT. Ils viennent d'une vue matérialisée
            que rien ne rafraîchit : un nombre sans sa date se lit comme un
            nombre d'aujourd'hui. Sonde D.877. */}
        {v.figeLe && ` Décomptes figés le ${new Date(v.figeLe).toLocaleString('fr-FR')}.`}
      </p>
    </>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   LA COLLECTIVITÉ — le tableau d'assemblage.
   ════════════════════════════════════════════════════════════════════════════
   ⚠️ IL DIT QUELLE PLANCHE DEMANDER. C'est la première des deux séries qui,
   ensemble, publient un plan cadastral ; la seconde est la planche de section.
*/
function TableauDAssemblage(
  { collectivite, onOuvrirSection }:
  { collectivite: NonNullable<ContexteCarte['collectivite']>; onOuvrirSection: (code: string) => void },
) {
  const [a, setA] = useState<Assemblage | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [filtre, setFiltre] = useState('');

  useEffect(() => {
    let vivant = true;
    setA(null); setErreur(null); setFiltre('');
    /**
     * ⚠️ `limite=2000` PARCE QUE LA PLUS DENSE EN PORTE 1 552. Un plafond de
     * 500 aurait tronqué les collectivités rurales en silence — le champ
     * `tronque` le dit, mais un agent qui cherche « AGK » et ne la trouve pas
     * conclut qu'elle n'existe pas, pas qu'elle est la 1 400ᵉ.
     */
    appeler<Assemblage>(`/sections/?collectivite=${collectivite.sourceId}&limite=2000`)
      .then((r) => { if (vivant) setA(r); })
      .catch((e) => { if (vivant) setErreur(messageDe(e)); });
    return () => { vivant = false; };
  }, [collectivite.sourceId]);

  /**
   * ⚠️ CALCULÉE ICI, JAMAIS DEMANDÉE AU SERVEUR. La médiane, l'étendue et le
   * coefficient de variation se déduisent des hectares déjà envoyés : les
   * réclamer en plus ferait un second aller-retour pour une arithmétique que
   * le navigateur fait en une milliseconde — et surtout deux sources pour un
   * même nombre, qui divergeraient au premier filtre.
   */
  const stats = useMemo(() => {
    if (!a || a.sections.length === 0) return null;
    const ha = a.sections.map((x) => Number(x.hectares)).filter(Number.isFinite).sort((x, y) => x - y);
    if (ha.length === 0) return null;
    const total = ha.reduce((t, x) => t + x, 0);
    const moyenne = total / ha.length;
    const ecart = Math.sqrt(ha.reduce((t, x) => t + (x - moyenne) ** 2, 0) / ha.length);
    return {
      total, min: ha[0]!, max: ha[ha.length - 1]!,
      mediane: ha[Math.floor(ha.length / 2)]!,
      cv: moyenne > 0 ? ecart / moyenne : 0,
      grille: a.sections.filter((x) => x.origine === 'grille').length,
      vides: a.sections.filter((x) => x.parcelles === 0).length,
      gelees: a.sections.filter((x) => x.gelee_le !== null).length,
    };
  }, [a]);

  const f = filtre.trim().toUpperCase();
  const liste = a ? a.sections.filter((x) => !f || x.code.includes(f)) : [];

  return (
    <>
      <div className="panneau-cadastral-tete">
        <h3>Tableau d’assemblage</h3>
        <p>
          {a ? `${a.assemblage.sections.toLocaleString('fr-FR')} sections` : '—'}
          {a?.assemblage.hectares && ` · ${nb(Number(a.assemblage.hectares), 0)} ha`}
          {' · '}{collectivite.parcelles === 0
            ? 'aucune parcelle au registre'
            : `${collectivite.parcelles.toLocaleString('fr-FR')} parcelles`}
        </p>
        <input value={filtre} onChange={(e) => setFiltre(e.target.value)}
               placeholder="chercher une section — ex. ACK"
               aria-label="Chercher une section" />
      </div>

      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      {!a && !erreur && <p className="mention">Lecture du tableau d’assemblage…</p>}

      {a && (
        <>
          <div className="panneau-cadastral-liste">
            {liste.map((x) => (
              <button key={x.code} type="button"
                      className={`panneau-cadastral-ligne${x.parcelles === 0 ? ' vide' : ''}`}
                      onClick={() => onOuvrirSection(x.code)}>
                <b>{x.code}</b>
                <span className="panneau-cadastral-jauge">
                  <span style={{ width: `${stats ? Math.max(3, (Number(x.hectares) / stats.max) * 100) : 0}%` }} />
                </span>
                <i>
                  {nb(Number(x.hectares))} ha
                  {x.origine === 'grille' && <em title="Tracée au carroyage, pas bornée sur le terrain."> ⌗</em>}
                </i>
              </button>
            ))}
            {liste.length === 0 && <p className="mention">Aucune section ne porte ce code.</p>}
          </div>

          {a.tronque && (
            <p className="panneau-cadastral-note avert">
              Liste plafonnée : toutes les sections ne sont pas montrées.
            </p>
          )}

          {stats && (
            <div className="panneau-cadastral-detail">
              <dl>
                <dt>Dimensions</dt>
                <dd>{nb(stats.min)} → {nb(stats.max)} ha · médiane {nb(stats.mediane)} · c.v. {nb(stats.cv, 2)}</dd>
                <dt>Carroyage</dt>
                <dd>{stats.grille.toLocaleString('fr-FR')} section(s) sur {a.sections.length}</dd>
                <dt>Sans parcelle</dt><dd>{stats.vides.toLocaleString('fr-FR')}</dd>
                <dt>État</dt>
                <dd>{stats.gelees === 0 ? 'brouillon — aucune gelée' : `${stats.gelees} gelée(s)`}</dd>
              </dl>
            </div>
          )}

          <div className="actions">
            {/* ⚠️ UN VRAI LIEN, PAS UN `fetch`. Le service et l'écran sont la même
                origine et le jeton vit dans un cookie httpOnly : un `<a href>`
                emporte l'authentification sans qu'une ligne de JavaScript touche
                au jeton. */}
            <a className="bouton" target="_blank" rel="noreferrer"
               href={`/sections/assemblage?collectivite=${collectivite.sourceId}&format=a3_paysage`}>
              Imprimer le tableau (A3)
            </a>
          </div>

          {/* ⚠️ CETTE PHRASE N'EST PAS DU REMPLISSAGE : sans elle, un agent qui
              voit des limites droites au milieu d'une ville conclut que le plan
              est faux. Elle dit POURQUOI une section est bornée par une ligne
              qui n'existe pas sur le terrain. */}
          {stats && (
            <p className="panneau-cadastral-note">
              {stats.grille.toLocaleString('fr-FR')} section(s) sur {a.sections.length} ont au moins une
              limite tracée par le carroyage : là où aucune rue ne borne — zones humides, emprises
              portuaires ou aéroportuaires, terrains non lotis. Les autres ne sont bornées que par
              des rues, des routes ou la limite de la collectivité.
              {stats.gelees === 0 && ' Un découpage non gelé se recalcule : une fois gelé, ni le code ni le contour d’une section ne bougent.'}
            </p>
          )}
        </>
      )}
    </>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   LA SECTION — sa fiche, ses feuilles, ses parcelles.
   ════════════════════════════════════════════════════════════════════════════ */

/**
 * IMPRIMER UNE PLANCHE DE SECTION.
 *
 * ⚠️ LE NOMBRE DE FEUILLES SE DEMANDE AVANT, PAS APRÈS. Une section dense sort
 * en plusieurs feuilles (ADK, Labé : 4 au 1:5 000) ; offrir un seul bouton
 * « Imprimer » livrerait la feuille 1 et laisserait croire à la feuille unique.
 * Le PDF s'ouvre dans un onglet : personne n'y lit l'en-tête HTTP qui le dit.
 *
 * ⚠️ ET LE LIEN EST UN VRAI LIEN, PAS UN `fetch`. Le service et l'écran sont la
 * même origine et le jeton vit dans un cookie httpOnly : un `<a href>` emporte
 * l'authentification sans qu'une ligne de JavaScript touche au jeton. Passer
 * par `fetch` obligerait à fabriquer un blob en mémoire — un PDF de 1,3 Mo par
 * feuille, pour le même résultat.
 *
 * ⚠️ EXPORTÉ DEPUIS LE 23/09/2026, ET C'EST DÉLIBÉRÉ. Le fondateur : « on doit
 * pouvoir aussi imprimer la section ». La fiche d'une parcelle connaît sa
 * section et sa circonscription — elle doit donc pouvoir en tirer la planche,
 * sans qu'on aille rouvrir le panneau du plan. Recopier ce bloc là-bas en
 * ferait DEUX règles de découpage, de format et de plafond : elles divergent
 * au premier correctif appliqué à une seule. Un seul composant, deux points
 * de montage.
 */
export function ImprimerLaPlanche(
  { code, circ, onFeuilles, echelleEcran = null, onTenirEchelle }:
  {
    code: string; circ: number;
    /**
     * LE CADRE DU TIRAGE, REMONTÉ POUR ÊTRE DESSINÉ — D.980. La route sert
     * `empriseDesFeuilles` précisément « pour que la carte puisse dessiner le
     * cadre de CE qui sortira, au lieu de le reconstruire » : l'écran le
     * transmet TEL QUEL, et le rappelle à `null` quand le bloc se démonte —
     * un cadre qui survit à sa fiche dessinerait le tirage d'une section
     * qu'on ne regarde plus.
     */
    onFeuilles?: (c: CadreFeuilles | null) => void;
    /** L'échelle tenue à l'écran (verrou) — pour dire l'écart avec la feuille. */
    echelleEcran?: number | null;
    /** Tenir l'écran à l'échelle de LA PLANCHE — c'est le contrat D.945. */
    onTenirEchelle?: (denominateur: number) => void;
  },
) {
  const [f, setF] = useState<Feuilles | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [format, setFormat] = useState('a3_paysage');

  useEffect(() => {
    let vivant = true;
    setF(null); setErreur(null);
    appeler<Feuilles>(`/sections/${code}/feuilles?collectivite=${circ}&format=${format}`)
      .then((r) => { if (vivant) setF(r); })
      .catch((e) => { if (vivant) setErreur(messageDe(e)); });
    return () => { vivant = false; };
  }, [code, circ, format]);

  /* Le cadre suit la réponse — une seule source, celle que le PDF emploiera. */
  useEffect(() => {
    if (!onFeuilles) return;
    onFeuilles(f ? { echelle: f.echelle, emprises: f.empriseDesFeuilles ?? [] } : null);
    return () => { onFeuilles(null); };
  }, [f, onFeuilles]);

  const lien = (feuille: number) =>
    `/sections/${code}/planche?collectivite=${circ}&format=${format}&feuille=${feuille}`;

  return (
    <div className="bloc">
      <div className="actions-ligne">
        <label className="champ-label" htmlFor={`fmt-${code}`}>Format</label>
        <select id={`fmt-${code}`} value={format} onChange={(e) => setFormat(e.target.value)}>
          <option value="a4_paysage">A4 paysage</option>
          <option value="a3_paysage">A3 paysage</option>
          <option value="a2_paysage">A2 paysage</option>
        </select>
      </div>

      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      {!f && !erreur && <p className="mention">Calcul du découpage…</p>}

      {f && (
        <>
          <p className="mention">
            {f.vide
              /* ⚠️ 81 % des sections du pays sont dans ce cas : la feuille se
                 délivre quand même, et c'est elle qui ATTESTE qu'il n'y a
                 rien. Ne pas l'offrir laisserait croire à une panne. */
              ? "Aucune parcelle au registre dans cette section — la feuille l’atteste, elle ne l’invente pas."
              : `${f.parcelles.toLocaleString('fr-FR')} parcelle(s) · 1:${f.echelle.toLocaleString('fr-FR')} · ${f.formatLibelle}`}
            {f.feuilles > 1 && ` · ${f.feuilles} feuilles (${f.colonnes}×${f.lignes})`}
          </p>
          {f.feuilles > 1 && (
            <p className="mention">
              L’échelle est choisie pour que les numéros soient LISIBLES : à l’échelle qui ferait
              tenir la section sur une seule feuille, aucun numéro n’entrerait dans sa parcelle.
            </p>
          )}
          {/* ⚠️ L'ÉCHELLE EST UN CONTRAT — D.945. Le cadre dessiné et le PDF
              sortent de la MÊME réponse ; reste l'écran. Quand il est tenu
              ailleurs, on le DIT — et un bouton le tient au nombre de la
              feuille. C'est la feuille qui fait foi, jamais l'écran. */}
          {onTenirEchelle && echelleEcran !== f.echelle && (
            <p className="mention">
              {echelleEcran
                ? `L’écran est tenu au 1/${echelleEcran.toLocaleString('fr-FR')} ; cette planche sort au 1/${f.echelle.toLocaleString('fr-FR')}.`
                : `L’écran est libre ; cette planche sort au 1/${f.echelle.toLocaleString('fr-FR')}.`}{' '}
              <button type="button" className="lien" onClick={() => onTenirEchelle(f.echelle)}>
                Tenir l’écran au 1/{f.echelle.toLocaleString('fr-FR')}
              </button>
            </p>
          )}
          {(f.empriseDesFeuilles ?? []).length === 0 && f.feuilles > 0 && (
            /* ⚠️ Une liste vide n'est pas une panne : au-delà du plafond de
               40 feuilles, le moteur ne composera pas ce document non plus.
               Le dire, plutôt que de ne rien dessiner en silence. */
            <p className="mention">
              Le cadre du tirage n’est pas dessinable : ce découpage dépasse le
              plafond du moteur de planche.
            </p>
          )}
          <div className="actions">
            {Array.from({ length: f.feuilles }, (_, i) => i + 1).map((n) => (
              <a key={n} className="bouton" href={lien(n)} target="_blank" rel="noreferrer">
                {f.feuilles > 1 ? `Feuille ${n}/${f.feuilles}` : 'Imprimer la planche'}
              </a>
            ))}
            <a className="bouton" href={`${lien(1)}&apercu=1`} target="_blank" rel="noreferrer">
              Aperçu (SVG, ne vaut pas document)
            </a>
          </div>
        </>
      )}
    </div>
  );
}

function ParcellesDeLaSection(
  { code, circ, onOuvrirParcelle }:
  { code: string; circ: number; onOuvrirParcelle?: (id: string) => void },
) {
  const [p, setP] = useState<Array<Record<string, string | null>> | null>(null);
  useEffect(() => {
    let vivant = true;
    setP(null);
    void appeler<{ parcelles: Array<Record<string, string | null>> }>(
      `/sections/${code}/parcelles?collectivite=${circ}&limite=50`)
      .then((r) => { if (vivant) setP(r.parcelles); })
      .catch(() => { if (vivant) setP([]); });
    return () => { vivant = false; };
  }, [code, circ]);

  if (p === null) return <p className="mention">Lecture des parcelles…</p>;
  if (p.length === 0) return <p className="mention">Aucune parcelle dans cette section.</p>;
  return (
    <table className="tableau">
      <thead>
        <tr><th>Numéro de plan</th><th>Référence opposable</th><th>Surface m²</th><th>Origine</th></tr>
      </thead>
      <tbody>
        {p.map((x) => (
          /* ⚠️ La ligne est un ARRÊT DU CLAVIER — D.948 : le parcours « de la
             section jusqu'à l'export » ne doit pas exiger une souris ici. */
          <tr key={x.identifiant as string}
              tabIndex={onOuvrirParcelle ? 0 : undefined}
              onClick={() => onOuvrirParcelle?.(x.identifiant as string)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target === e.currentTarget) {
                  e.preventDefault(); onOuvrirParcelle?.(x.identifiant as string);
                }
              }}
              style={onOuvrirParcelle ? { cursor: 'pointer' } : undefined}>
            <td><strong>{x.section} {x.numero_cadastral}</strong></td>
            <td>{x.reference_cadastrale ?? (
              <span className="mention" title="Aucun droit n’y est inscrit : la référence opposable s’alloue à l’immatriculation.">
                non immatriculée
              </span>)}</td>
            <td>{surfaceEnFrancais(x.surface_m2)}</td>
            <td><span className="mention">{libelleOrigineParcelle(x.origine)}</span></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FicheSection(
  { section, onOuvrirParcelle, tiroir = null, onFeuilles, echelleEcran, onTenirEchelle, onRetenirSection }:
  {
    section: NonNullable<ContexteCarte['section']>;
    onOuvrirParcelle?: (id: string) => void;
    /** La section déjà montrée par le tiroir parcelle — voir ci-dessous. */
    tiroir?: { code: string; circ: number } | null;
    onFeuilles?: (c: CadreFeuilles | null) => void;
    echelleEcran?: number | null;
    onTenirEchelle?: (d: number) => void;
    /** Retenir les parcelles de la section dans la table — chemin CLAVIER (D.948). */
    onRetenirSection?: (code: string, circ: number) => void;
  },
) {
  /**
   * ⚠️⚠️ UN SEUL BLOC D'IMPRESSION VISIBLE À LA FOIS — item D.995. La règle
   * « un seul composant, deux points de montage » était tenue ; c'est la
   * SIMULTANÉITÉ d'affichage qui ne l'était pas : section en contexte ici,
   * parcelle de la MÊME section ouverte dans le tiroir — l'agent voyait le
   * même choix de format et les mêmes liens de feuille deux fois. Constaté
   * sur capture du fondateur, section AGC. Le geste le plus récent gagne
   * (ouvrir la parcelle), comme `ouvrirDossier` ferme le tiroir : quand le
   * tiroir montre la planche de CETTE section, ce bloc se tait et le DIT.
   * Deux sections différentes ne sont pas un doublon : les deux blocs restent.
   */
  const plancheAuTiroir = tiroir !== null
    && tiroir.code === section.code && tiroir.circ === section.collectivite;
  return (
    <>
      <div className="panneau-cadastral-tete">
        <h3>Section {section.code}</h3>
        <p>
          {section.origine === 'voie' ? 'bornée par les rues' : 'tracée au carroyage'}
          {' · '}{nb(section.surfaceM2 / 10000, 2)} ha
        </p>
      </div>
      <div className="panneau-cadastral-corps">
        {plancheAuTiroir ? (
          <p className="mention">
            L’impression de cette planche est affichée dans la fiche de la
            parcelle ouverte — un seul choix à l’écran.
          </p>
        ) : (
          <ImprimerLaPlanche code={section.code} circ={section.collectivite}
            onFeuilles={onFeuilles} echelleEcran={echelleEcran} onTenirEchelle={onTenirEchelle} />
        )}
        {onRetenirSection && (
          /* Le chemin CLAVIER de la sélection — D.948 : du choix de la section
             jusqu'à l'export, sans souris. La table reçoit les parcelles de
             CETTE section, depuis la base. */
          <button type="button" className="lien"
                  onClick={() => onRetenirSection(section.code, section.collectivite)}>
            Retenir les parcelles de cette section dans la sélection
          </button>
        )}
        <ParcellesDeLaSection code={section.code} circ={section.collectivite}
                              onOuvrirParcelle={onOuvrirParcelle} />
      </div>
      {section.origine !== 'voie' && (
        <p className="panneau-cadastral-note">
          Cette section est tracée au carroyage : ses limites sont un découpage de travail, pas des
          bornes relevées sur le terrain. 152 442 sections sur 153 248 sont dans ce cas.
        </p>
      )}
    </>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   LE PANNEAU
   ════════════════════════════════════════════════════════════════════════════ */
export function PanneauCadastral(
  { contexte, onAller, onOuvrirParcelle, onChoisirSection, onChoisirCollectivite,
    sectionDuTiroir = null, onFeuilles, echelleEcran = null, onTenirEchelle, onRetenirSection }:
  {
    contexte: ContexteCarte | null;
    onAller: (c: CibleCarte) => void;
    onOuvrirParcelle?: (id: string) => void;
    /** Ouvrir une section depuis le tableau : la carte y va et la retient. */
    onChoisirSection: (code: string) => void;
    /** Choisir une collectivité — le geste qui ouvre son assemblage (D.997). */
    onChoisirCollectivite: (c: ContexteCarte['collectivite']) => void;
    /** La section déjà montrée par le tiroir parcelle — exclusion D.995. */
    sectionDuTiroir?: { code: string; circ: number } | null;
    /** Le cadre du tirage, pour la carte — D.980. */
    onFeuilles?: (c: CadreFeuilles | null) => void;
    echelleEcran?: number | null;
    onTenirEchelle?: (d: number) => void;
    /** Retenir les parcelles d'une section dans la table — chemin CLAVIER (D.948). */
    onRetenirSection?: (code: string, circ: number) => void;
  },
) {
  /**
   * LE PANNEAU SE FERME, ET SON ÉTAT SE RETIENT.
   *
   * ⚠️ LE DÉFAUT PAR DÉFAUT DÉPEND DE LA FENÊTRE, et ce n'est pas du confort :
   * 360 px de panneau sur une fenêtre de 900 px laissent 540 px de carte —
   * moins que le panneau. Sous 1 100 px, on ouvre donc sur la carte.
   *
   * ⚠️ `localStorage` PEUT LEVER, pas seulement rendre vide : navigation
   * privée, site data bloqué, capture de vignette. Un accès non gardé ferait
   * écran blanc là où il ne manque qu'une préférence d'affichage.
   */
  const [ouvert, setOuvert] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem('cadastral.carte.panneau');
      if (v === 'ferme') return false;
      if (v === 'ouvert') return true;
    } catch { /* préférence indisponible : on décide sur la largeur */ }
    return typeof window === 'undefined' || window.innerWidth >= 1100;
  });
  const basculer = useCallback(() => {
    setOuvert((v) => {
      try { localStorage.setItem('cadastral.carte.panneau', v ? 'ferme' : 'ouvert'); } catch { /* sans mémoire */ }
      return !v;
    });
  }, []);

  /**
   * ⚠️ ÉCHAP FERME, ET C'EST UN POSTE DE TRAVAIL. Un agent y passe sa journée ;
   * atteindre une poignée de 16 px à la souris pour dégager la carte, cinquante
   * fois par jour, est un coût réel.
   */
  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => { if (e.key === 'Escape' && ouvert) basculer(); };
    window.addEventListener('keydown', auClavier);
    return () => window.removeEventListener('keydown', auClavier);
  }, [ouvert, basculer]);

  const collectivite = contexte?.collectivite ?? null;
  const section = contexte?.section ?? null;

  /**
   * ⚠️ L'ÉCHELON SUIT LA SÉLECTION, PLUS LE ZOOM — D.997. L'ancien seuil
   * (« sections moins deux ») existait parce que la collectivité arrivait par
   * le CENTRE à chaque déplacement ; choisie au clic, la regarder EST un
   * choix, à n'importe quel zoom.
   */
  const echelon: 'pays' | 'collectivite' | 'section' =
    section ? 'section'
    : collectivite ? 'collectivite'
    : 'pays';

  return (
    <aside className={`panneau-cadastral${ouvert ? '' : ' replie'}`}>
      <button className="panneau-cadastral-poignee" type="button" onClick={basculer}
              aria-expanded={ouvert}
              title={ouvert ? 'Replier le panneau (Échap)' : 'Ouvrir le panneau du plan cadastral'}>
        {ouvert ? '›' : '‹'}
      </button>

      {ouvert && (
        <div className="panneau-cadastral-dedans">
          {/* ⚠️ LE FIL D'ARIANE DIT OÙ L'ON EST *ET* COMMENT REMONTER. Sans lui,
              remonter d'un échelon demandait de dézoomer à l'aveugle jusqu'à ce
              que le panneau change de lui-même. */}
          <nav className="panneau-cadastral-fil" aria-label="Échelon du plan cadastral">
            <button type="button" className={`lien${echelon === 'pays' ? ' actif' : ''}`}
                    onClick={() => {
                      onChoisirSection('');
                      onChoisirCollectivite(null);
                      onAller({ centre: [-11.3, 10.4], zoom: 6, cle: `pays-${Date.now()}` });
                    }}>
              Pays
            </button>
            {collectivite && (
              <>
                <span aria-hidden="true">›</span>
                <button type="button" className={`lien${echelon === 'collectivite' ? ' actif' : ''}`}
                        onClick={() => onChoisirSection('')}>
                  {collectivite.nom}
                </button>
              </>
            )}
            {section && (
              <>
                <span aria-hidden="true">›</span>
                <span className="actif">Section {section.code}</span>
              </>
            )}
          </nav>

          {/* ⚠️ L'ÉCHELON ADMINISTRATIF EST DIT, ET CE N'EST PAS UN DÉTAIL. La
              Guinée compte 36 communes contre 304 sous-préfectures ; 91 % des
              sections pendent d'une SOUS-PRÉFECTURE. Écrire « commune » partout
              ferait lire un découpage communal là où il n'y en a pas. */}
          {collectivite && echelon !== 'pays' && (
            <p className="panneau-cadastral-echelon">
              {collectivite.echelon.replace(/_/g, '-')}
              {collectivite.code ? ` · code ${collectivite.code}` : ''}
              {collectivite.gelees > 0 && ` · ${collectivite.gelees} section(s) gelée(s)`}
            </p>
          )}

          {echelon === 'pays' && <VueDuPaysListe onAller={onAller} onChoisir={onChoisirCollectivite} />}
          {echelon === 'collectivite' && collectivite && (
            <TableauDAssemblage collectivite={collectivite} onOuvrirSection={onChoisirSection} />
          )}
          {echelon === 'section' && section && (
            <FicheSection section={section} onOuvrirParcelle={onOuvrirParcelle}
                          tiroir={sectionDuTiroir} onFeuilles={onFeuilles}
                          echelleEcran={echelleEcran} onTenirEchelle={onTenirEchelle}
                          onRetenirSection={onRetenirSection} />
          )}

          {/* ⚠️ L'ATTRIBUTION N'EST PAS RÉPÉTÉE ICI. Elle est calculée par
              `AttributionSources` dans la carte, depuis les couches RÉELLEMENT
              servies. L'ancien écran en portait deux textes écrits à la main,
              différents l'un de l'autre — c'est précisément ce que la fusion
              supprime. */}
        </div>
      )}
    </aside>
  );
}
